import React from 'react';
import type { PipelineStageId } from '../../lib/pipelineStages';

/**
 * The seven stage icons of the pipeline, drawn for it on one 24 px grid: 1.5 px strokes, round ends and joins, the same
 * corner radius and weight, so the row reads as one set. One idea per stage:
 *   ingest       uneven inputs pass a bar and leave as even lines (normalize)
 *   dedup        two overlapping records; the overlap is the duplicate
 *   score        a cluster of points inside a boundary, and one outlier
 *   classify     a label with a spark (the model tags what it found)
 *   consolidate  two paths meet the main line in one arrow
 *   plan         a clipboard with ordered steps
 *   execute      a shield with a check (acted on, then validated)
 */
const PATHS: Record<PipelineStageId, React.ReactNode> = {
  ingest: (
    <>
      <path d="M3.5 7h5.5M3.5 12h7.5M3.5 17h3.5" />
      <path d="M14 4.5v15" />
      <path d="M17.5 7h3M17.5 12h3M17.5 17h3" />
    </>
  ),
  dedup: (
    <>
      <circle cx="9" cy="12" r="5.5" />
      <circle cx="15" cy="12" r="5.5" />
    </>
  ),
  score: (
    <>
      <ellipse cx="10" cy="11" rx="6.5" ry="5.5" strokeDasharray="2.6 2.4" />
      <circle cx="7.5" cy="10" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="11" cy="13.5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="8.75" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="19.6" cy="18.8" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
  classify: (
    <>
      <path d="M12.2 3.5H5.5a2 2 0 0 0-2 2v6.7a2 2 0 0 0 .6 1.4l6.9 6.9a2 2 0 0 0 2.8 0l6.3-6.3a2 2 0 0 0 0-2.8L13.6 4.1a2 2 0 0 0-1.4-.6z" />
      <path d="M9 6.5c.3 1.5 1 2.2 2.5 2.5-1.5.3-2.2 1-2.5 2.5-.3-1.5-1-2.2-2.5-2.5 1.5-.3 2.2-1 2.5-2.5z" />
    </>
  ),
  consolidate: (
    <>
      <path d="M3.5 6h3.8c1.5 0 2.5.7 3.2 2l1 2c.6 1.3 1.6 2 3 2" />
      <path d="M3.5 18h3.8c1.5 0 2.5-.7 3.2-2l1-2c.6-1.3 1.6-2 3-2" />
      <path d="M3.5 12h17" />
      <path d="M17.5 8.5 21 12l-3.5 3.5" />
    </>
  ),
  plan: (
    <>
      <path d="M8 5.5H6.5A1.5 1.5 0 0 0 5 7v12.5A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V7a1.5 1.5 0 0 0-1.5-1.5H16" />
      <rect x="8" y="3.5" width="8" height="3.5" rx="1" />
      <path d="M8.5 11.5l1 1 2-2M13.5 11.5h2M8.5 16.5l1 1 2-2M13.5 16.5h2" />
    </>
  ),
  execute: (
    <>
      <path d="M12 3.2 19 6v5.6c0 4.3-2.9 7.9-7 9.3-4.1-1.4-7-5-7-9.3V6l7-2.8z" />
      <path d="m9 12.2 2.1 2.1 4-4.1" />
    </>
  ),
};

const PipelineIcon: React.FC<{ id: PipelineStageId; size?: number }> = ({ id, size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {PATHS[id]}
  </svg>
);

export default PipelineIcon;
